'use client';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Frag } from './Frag';
import { COLORS, FONT, FONTS } from './tokens';

// ── FRACTION BOARD — an ACCUMULATING worked-example board (the "real teacher whiteboard" model) ──────
// A teaching step's board is a worked example that BUILDS UP line by line, exactly like a teacher
// filling a whiteboard: earlier lines stay VISIBLE (dimmed), the current line is HIGHLIGHTED. WHICH
// line is active is decided by the ENGINE — each key line is its own gate:'ack' teach step carrying
// `stimulus.data.active` — so the reveal is exactly synced to Emma's per-step speech and can NEVER
// drift (no client-side timer). The board keeps accumulating across steps; it only "erases" when a
// NEW board starts (a different `data.board` id → EmmaBoard re-keys and this remounts).
//
// This replaces the earlier one-beat-at-a-time FractionBeats scene: pedagogy review (+ GPT-5.5, +
// founder) preferred the accumulating board — the kid sees 1/3 → 2/6 → 2/6+1/6=3/6 all at once and
// connects the steps without juggling memory across slides. Risk to manage (GPT's note): clutter —
// so we dim prior lines, highlight the active one, and erase between problems.

const CHALK = COLORS.chalk, WRONG = COLORS.wrong, RIGHT = COLORS.right, ACCENT = COLORS.accent;
const colorFill = (c?: string) => (c === 'blue' ? COLORS.bGrad : COLORS.aGrad);

// A LINE is one thing written on the board. `kind` picks the visual. All lines stack vertically.
export interface FractionLine {
  kind: 'equation' | 'bars' | 'cut' | 'method' | 'align' | 'note';
  equation?: string;                 // KaTeX (kind:'equation', or a caption under bars)
  note?: string;                     // small caption
  noteKind?: 'good' | 'bad' | '';
  bars?: { parts: number; shaded: number; label?: string; color?: 'orange' | 'blue' }[];
  plus?: boolean;                    // '+' between two bars
  cut?: { fromParts: number; fromShaded: number; toParts: number; toShaded: number; color?: 'orange' | 'blue'; ghost?: boolean; label?: string };
  method?: { rows: string[]; highlight?: number; rowTriggers?: string[] };
  align?: { parts: number; shaded: number; label: string; color?: 'orange' | 'blue' }[];
}

export interface FractionBoardData {
  board: string;                     // board id — changes → EmmaBoard erases & remounts (new problem)
  title?: string;                    // small heading for this board (e.g. "Add 1/3 + 1/6")
  problemStrip?: string;             // PROBLEM STRIP (2026-07-30, GPT+Gemini flow fix): a persistent
                                     // KaTeX line pinned at the TOP of a one-idea-per-screen board, so
                                     // the kid never loses the original problem when the board erases
                                     // between steps (e.g. "1/3 + 1/6 = ?"). Small + dimmed; not narrated.
  lines: FractionLine[];             // the full worked example, in order
  active: number;                    // reveal lines 0..active; `active` is highlighted, rest dimmed
}

// WORD-SYNCED PIECE HIGHLIGHT (2026-07-29, founder rule): a bar's SHADED piece lights up the moment Emma
// SAYS that piece ("one third" → the 1/3 piece glows). `spoken` is true when this bar's fraction is in the
// words Emma has said so far. The whole bars share one length (same whole), so a lit third-piece reads
// visibly WIDER than a lit sixth-piece — that's how a kid SEES "different sizes". `spoken=undefined` → no
// glow (nothing said yet); once spoken it stays lit (monotonic upstream). General: works for any bar.
function Bar({ parts, shaded, color, width = 240, spoken = false }: { parts: number; shaded: number; color?: string; width?: number; spoken?: boolean }) {
  const glow = color === 'blue' ? 'rgba(120,170,255,.95)' : 'rgba(255,176,103,.98)';
  return (
    <motion.div
      animate={{ scale: spoken ? 1.04 : 1 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      style={{ display: 'flex', border: `2px solid ${spoken ? glow : '#9aa6a0'}`, borderRadius: 8, overflow: 'hidden', height: 42, width, boxShadow: spoken ? `0 0 18px ${glow}` : '0 3px 10px rgba(0,0,0,.22)' }}
    >
      {Array.from({ length: parts }, (_, i) => {
        const isShaded = i < shaded;
        return (
          <div key={i} style={{
            flex: 1,
            borderRight: i < parts - 1 ? '1px solid rgba(0,0,0,.25)' : 'none',
            background: isShaded ? colorFill(color) : 'transparent',
            // Outline the shaded piece only while its fraction is being SPOKEN — draws the eye to that exact piece.
            boxShadow: isShaded && spoken ? `inset 0 0 0 2px ${glow}` : 'none',
            transition: 'background .4s, box-shadow .3s',
            zIndex: isShaded ? 1 : 0,
          }} />
        );
      })}
    </motion.div>
  );
}

function BarWrap({ parts, shaded, label, color, spoken = false }: { parts: number; shaded: number; label?: string; color?: string; spoken?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <Bar parts={parts} shaded={shaded} color={color} spoken={spoken} />
      {label && <div style={{ fontFamily: FONTS.hand, fontSize: FONT.label, color: spoken ? (color === 'blue' ? '#BcD6FF' : '#FFD9AA') : CHALK, fontWeight: spoken ? 700 : 400 }}><Frag text={label} size={FONT.label} /></div>}
    </div>
  );
}

// The cut: renders fromParts→toParts with a knife sweep + ghost outline of the original footprint.
// Static once revealed (the "amount stays, name changes" moment); animates on first reveal via key.
// `label` is PLAIN TEXT (no KaTeX) — mixing \tfrac with prose in one Frag mangles the prose into
// run-together italic math. If a fraction is needed in the label, write it as "2/6" plain.
function CutLine({ spec }: { spec: NonNullable<FractionLine['cut']> }) {
  const ghostW = spec.ghost ? `${(spec.fromShaded / spec.fromParts) * 100}%` : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div style={{ position: 'relative' }}>
        <Bar parts={spec.toParts} shaded={spec.toShaded} color={spec.color} />
        {ghostW && (
          <div style={{ position: 'absolute', left: 0, top: -3, bottom: -3, width: ghostW, border: '2px dashed rgba(255,255,255,.85)', borderRadius: 8, pointerEvents: 'none' }} />
        )}
      </div>
      {spec.label && <div style={{ fontFamily: FONTS.hand, fontSize: FONT.label, color: CHALK }}>{spec.label}</div>}
    </div>
  );
}

function noteColor(kind?: string) { return kind === 'bad' ? WRONG : kind === 'good' ? RIGHT : ACCENT; }

// FRACTION → SPOKEN PHRASES (2026-07-29): map a bar's shaded/parts to the ways Emma might SAY it, so we
// can light the exact piece when she says it. e.g. 1/3 → ["one third"], 2/6 → ["two sixths"]. Covers the
// small fractions these lessons use. If nothing matches, the piece just doesn't glow (safe default).
const ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'];
const DENOM = { 2: 'half', 3: 'third', 4: 'fourth', 5: 'fifth', 6: 'sixth', 8: 'eighth', 10: 'tenth', 12: 'twelfth' } as Record<number, string>;
function fractionSpoken(shaded: number, parts: number): string[] {
  const top = ONES[shaded]; const d = DENOM[parts];
  if (!top || !d) return [];
  const plural = shaded === 1 ? d : d + 's';           // "one third" | "two thirds"
  const phrases = [`${top} ${plural}`];
  if (shaded === 1 && parts === 2) phrases.push('one half', 'a half');
  return phrases;
}
// Is this bar being spoken right now? (its fraction phrase appears in the words Emma has said so far)
function barSpoken(shaded: number, parts: number, said: string): boolean {
  if (!said) return false;
  const s = said.toLowerCase();
  return fractionSpoken(shaded, parts).some((p) => s.includes(p));
}

// Render one line's visual (used for every stacked line). `said` = words Emma has spoken so far (for the
// word-synced piece highlight).
function LineView({ line, said = '' }: { line: FractionLine; said?: string }) {
  switch (line.kind) {
    case 'equation':
      return <div style={{ fontSize: FONT.math }}><Frag text={line.equation || ''} size={FONT.math} /></div>;
    case 'bars':
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          {line.bars?.[0] && <BarWrap {...line.bars[0]} color={line.bars[0].color} spoken={barSpoken(line.bars[0].shaded, line.bars[0].parts, said)} />}
          {line.plus && <div style={{ fontSize: FONT.math, opacity: 0.8 }}><Frag text="+" size={FONT.math} /></div>}
          {line.bars?.[1] && <BarWrap {...line.bars[1]} color={line.bars[1].color} spoken={barSpoken(line.bars[1].shaded, line.bars[1].parts, said)} />}
        </div>
      );
    case 'align':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
          {line.align?.map((a, i) => <BarWrap key={i} {...a} color={a.color} spoken={barSpoken(a.shaded, a.parts, said)} />)}
        </div>
      );
    case 'cut':
      return line.cut ? <CutLine spec={line.cut} /> : null;
    case 'method': {
      // Center the method block as a column, each row sized to its OWN text (alignItems:center).
      // WORD-SYNCED ROWS (founder: "emphasize text as Emma talks"): if the method carries `rowTriggers`
      // (one phrase per row), the rows WRITE IN and GLOW one at a time — the active row is the LAST one
      // whose trigger phrase Emma has already spoken (`said`), rows past it stay hidden until she reaches
      // them. No rowTriggers → the old static `highlight` (fully backward-compatible). Monotonic within a step.
      const m = line.method;
      const rows = m?.rows || [];
      const rowSync = Array.isArray(m?.rowTriggers) && (m?.rowTriggers?.length || 0) > 0;
      // ALL rows are ALWAYS visible (founder: "the text should always be there — you just highlight it as you
      // speak"). The highlight simply MOVES to the row Emma is currently saying (last matched trigger). No reveal,
      // no hiding. Without rowTriggers → the static `highlight` row (unchanged).
      let activeRow = typeof m?.highlight === 'number' ? m!.highlight! : -1;
      if (rowSync) { const s = (said || '').toLowerCase(); activeRow = -1; rows.forEach((_, i) => { const t = m!.rowTriggers![i]; if (t && s.includes(t.toLowerCase())) activeRow = i; }); }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center',
          fontFamily: FONTS.hand, fontSize: FONT.label, textAlign: 'center' }}>
          {rows.map((r, i) => {
            const hi = i === activeRow; // only the line she's saying glows; the rest stay visible + calm
            return (
              <div key={i} style={{ padding: '6px 14px', borderRadius: 10, border: '1px solid transparent',
                transition: 'background .3s, box-shadow .3s, color .3s, border-color .3s',
                ...(hi
                  ? { background: 'rgba(255,176,103,.20)', borderColor: ACCENT, color: '#FFE7C6', boxShadow: `0 0 20px ${ACCENT}66` }
                  : { color: CHALK, opacity: 0.9 }) }}>{r}</div>
            );
          })}
        </div>
      );
    }
    case 'note':
      return <div style={{ fontFamily: FONTS.hand, fontSize: FONT.note, color: noteColor(line.noteKind), textAlign: 'center', maxWidth: 620 }}>{line.note}</div>;
    default:
      return null;
  }
}

// The accumulating board: renders every line up to `active`. Prior lines dim (still readable), the
// active line is bright + gently scales in. New boards remount (via EmmaBoard key on data.board).
// The available height for the board card inside EmmaBoard's board area. The area has ~180px top +
// ~170px bottom padding on an ~760px-tall stage → ~ this much usable. We AUTO-SCALE the card down if
// its natural content is taller, so ALL accumulated lines stay visible (like a teacher writing smaller
// to fit the whole worked example) — never a scrollbar, never off-screen. Tunable in one place.
const BOARD_MAX_H = 440; // px of usable height for the card

export function FractionBoard({ data, spokenNow = '' }: { data: FractionBoardData; spokenNow?: string }) {
  const lines = data?.lines || [];
  const active = typeof data?.active === 'number' ? data.active : lines.length - 1;

  // Measure natural content height; if it exceeds BOARD_MAX_H, scale the card down to fit.
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const natural = el.scrollHeight;
    setScale(natural > BOARD_MAX_H ? Math.max(0.55, BOARD_MAX_H / natural) : 1);
  }, [active, lines.length]);

  return (
    // Outer wrapper reserves the scaled footprint so the scaled card stays centered in the board area.
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', transform: `scale(${scale})`, transformOrigin: 'center center', transition: 'transform .3s ease' }}>
      <div ref={innerRef} style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.18)', borderRadius: 20, padding: '24px 40px', boxShadow: '0 10px 34px rgba(0,0,0,.28)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, minWidth: 460, maxWidth: 760 }}>
        {/* PROBLEM STRIP: persistent problem pill so one-idea-per-screen boards keep the kid oriented. */}
        {data.problemStrip && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 14px', borderRadius: 999,
            background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.14)', opacity: 0.72 }}>
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,.55)', fontWeight: 700 }}>solving</span>
            <Frag text={data.problemStrip} size={22} />
          </div>
        )}
        {data.title && <div style={{ fontFamily: FONTS.hand, fontSize: FONT.label, color: ACCENT, opacity: 0.95 }}>{data.title}</div>}
        {lines.slice(0, active + 1).map((line, i) => {
          const isActive = i === active;
          // A method line with rowTriggers word-syncs its rows to Emma's speech, so it must get `spokenNow`
          // and stay bright even when it isn't the line-level active line (else its row glow would be dimmed).
          const rowSync = line.kind === 'method' && Array.isArray(line.method?.rowTriggers) && (line.method?.rowTriggers?.length || 0) > 0;
          const lit = isActive || rowSync;
          return (
            <motion.div
              key={i}
              initial={isActive ? { opacity: 0, y: 8 } : false}
              animate={{ opacity: lit ? 1 : 0.42, y: 0 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, filter: lit ? 'none' : 'saturate(.7)' }}
            >
              <LineView line={line} said={lit ? spokenNow : ''} />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
